import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, ArrowLeft, BookOpen } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import BackToTop from '@/components/BackToTop';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopVisible, setShopVisible] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then(r => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/settings`)
      .then(r => r.json())
      .then(d => { if (d.shop_visible === false) setShopVisible(false); })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead
        title="Web Design &amp; Print On Demand Blog | Tips for Bournemouth Businesses | UI Designs"
        description="Expert web design tips, affordable website advice, local SEO strategies, and digital marketing guides for small businesses in Bournemouth, Poole, Christchurch, and across Dorset."
        canonical="https://uidesigns.co.uk/blog"
        keywords="web design blog, custom print on demand, branded merchandise, Bournemouth business tips, local SEO advice, small business website tips"
      />
      <Navigation />

      <main>
        {/* Dark header — matches legal pages */}
        <div className="relative bg-[#0A0A0A] pt-32 pb-16 md:pt-36 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
          <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-[#00E5FF]/5 rounded-full blur-[120px]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#00E5FF] transition-colors mb-8" data-testid="blog-back-link">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <h1 data-testid="blog-page-title" className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                Insights & Advice
              </h1>
            </div>
            <p className="text-base sm:text-lg text-slate-400 max-w-xl mt-3">
              Practical tips on web design, local SEO, and growing your Bournemouth business online.
            </p>
          </div>
        </div>

        <section data-testid="blog-list" className="pt-12 pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-slate-50 dark:bg-white/5 rounded-2xl h-72 animate-pulse" />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <p className="text-slate-500 text-center py-20">No posts yet. Check back soon!</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, i) => (
                  <ScrollReveal key={post.slug} delay={i * 80}>
                    <Link
                      to={`/blog/${post.slug}`}
                      data-testid={`blog-card-${i}`}
                      className="group block bg-white dark:bg-[#111111] border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden glow-hover hover:border-[#00E5FF]/30 transition-all"
                    >
                      {post.cover_image && (
                        <div className="aspect-[16/9] overflow-hidden bg-slate-100 dark:bg-white/5">
                          <img
                            src={post.cover_image}
                            alt={`${post.title} — web design Bournemouth blog`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        {post.tag && (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-[#00B4D8] mb-3 block">{post.tag}</span>
                        )}
                        <h2 className="font-['Outfit'] text-lg font-bold text-[#0A0A0A] dark:text-white mb-2 group-hover:text-[#00B4D8] transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 line-clamp-2">{post.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00B4D8] group-hover:gap-2 transition-all">
                            Read <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-[#F8F9FA] dark:bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-6">Explore our services</p>
              <div className="flex flex-wrap gap-3">
                {shopVisible && <Link to="/shop" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Custom Print Shop</Link>}
                <Link to="/web-design-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Web Design Bournemouth</Link>
                <Link to="/web-design-barbers-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Barber Websites</Link>
                <Link to="/web-design-gyms-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Gym Websites</Link>
                <Link to="/web-design-restaurants-bournemouth" className="px-5 py-2 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:border-[#00E5FF] hover:text-[#00B4D8] transition-colors">Restaurant Websites</Link>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}
